import clsx from 'clsx'
import { HidingHeader } from 'hiding-header-react'
import 'hiding-header/dist/style.css'
import Image from 'next/image'
import Link from 'next/link'
import { useRouter } from 'next/router'
import { type FunctionComponent, useEffect, useRef, useState } from 'react'
import { HeaderSearchPopup } from 'ui'
import type { PageProps } from '../pages/[[...path]]'
import { contemberLinkToHref } from '../utilities/contemberLinkToHref'
import { headerLinkToHrefTargetRel } from '../utilities/headerLinkToHref'
import { Button } from './Button'
import styles from './Header.module.sass'
import { HeaderMobileMenu } from './HeaderMobileMenu'
import { HeaderNestedLinks } from './HeaderNestedLinks'
import { HeaderSubmenuProducts } from './HeaderSubmenuProducts'
import { Icon } from './Icon'
import { SitePicker } from './SitePicker'
import { useSite } from './contexts/SiteContextProvider'
import { useTranslate } from './contexts/TranslationsContextProvider'

export type HeaderProps = NonNullable<PageProps['header']>

export const Header: FunctionComponent<HeaderProps> = ({ logo, links, contactPageLink, siteTitle, listSite }) => {
	const { siteCode, baseUrl } = useSite()
	const translation = useTranslate()
	const websiteLocale = siteCode === 'cz' ? 'cz' : siteCode // weird !!

	const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)
	const [activeLinkPosition, setActiveLinkPosition] = useState<{ offsetLeft: number | null; width: number } | null>(
		null,
	)
	const headerRef = useRef<HTMLHeadElement | null>(null)
	const [headerCount, setHeaderCount] = useState(0)
	const [hoveredLinkPosition, setHoveredLinkPosition] = useState<{
		offsetLeft: number | null
		width: number | null
	} | null>(null)
	const [isSearchOpen, setIsSearchOpen] = useState(false)

	// Force re-mount of the header when route changes to correctly recalculate link positions
	const router = useRouter()
	// biome-ignore lint/correctness/useExhaustiveDependencies: we need to rerender header on every route change
	useEffect(() => {
		setHeaderCount((count) => count + 1)
	}, [router.asPath])

	// When all links are inactive, hide the bottom indicator line
	useEffect(() => {
		if (links?.every((link) => !link?.isActive)) {
			setActiveLinkPosition(null)
		}
	}, [links])

	return (
		<div className={styles.headWrapper}>
			<div className={styles.head}>
				<HidingHeader>
					<header
						key={headerCount}
						className={clsx(styles.wrapper, (hoveredLinkPosition || activeLinkPosition) && styles.is_lineVisible)}
						style={{
							'--Header-activeLink-offsetLeft':
								hoveredLinkPosition?.offsetLeft || activeLinkPosition?.offsetLeft
									? `${(hoveredLinkPosition?.offsetLeft || activeLinkPosition?.offsetLeft) ?? 0}px`
									: '50%',
							'--Header-activeLink-width': `${(hoveredLinkPosition?.width || activeLinkPosition?.width) ?? 0}px`,
						}}
						ref={headerRef}
					>
						<div className={styles.in}>
							{logo && (
								<Link
									className={styles.logoLink}
									href={`https://www.almacareer.com${baseUrl.startsWith('/') ? baseUrl : `/${baseUrl}`}`}
								>
									<Image src={logo.url} alt="Logo" width={logo.width} height={logo.height} />
								</Link>
							)}
							<div className={styles.linksWrapper}>
								<ul
									className={styles.links}
									onMouseLeave={() => {
										if (!activeLinkPosition) {
											setHoveredLinkPosition({ offsetLeft: hoveredLinkPosition?.offsetLeft ?? null, width: null })
										} else {
											setHoveredLinkPosition(null)
										}
									}}
								>
									{links?.map(
										(link) =>
											link && (
												<HeaderLink
													key={link.id}
													link={link}
													setHoveredLinkPosition={setHoveredLinkPosition}
													setActiveLinkPosition={setActiveLinkPosition}
												/>
											),
									)}
								</ul>
								<button type="button" className={styles.search} onClick={() => setIsSearchOpen(true)}>
									<Icon name="search" />
								</button>
							</div>

							<div className={styles.column}>
								{contactPageLink && (
									<div className={styles.button}>
										<Button
											type="link"
											size="small"
											variant="primary"
											href={contactPageLink.externalLink ?? contactPageLink.internalLink?.url ?? ''}
										>
											{contactPageLink.title ?? translation('header.contactLabel')}
										</Button>
									</div>
								)}
								<div className={styles.switcher}>
									<SitePicker site={siteTitle} otherSites={listSite} />
								</div>
							</div>

							<button type="button" className={styles.sandwich} onClick={() => setIsMobileMenuOpen(true)}>
								<Icon name="sandwich" />
							</button>
						</div>
					</header>
					<div className={styles.bottomBorder} />
				</HidingHeader>

				<HeaderMobileMenu
					isOpen={isMobileMenuOpen}
					setIsOpen={setIsMobileMenuOpen}
					links={links}
					contactPageLink={contactPageLink?.externalLink}
				/>

				<HeaderSearchPopup
					type="magazine"
					isOpen={isSearchOpen}
					onClose={() => setIsSearchOpen(false)}
					locale={websiteLocale}
				/>
			</div>
		</div>
	)
}

const HeaderLink: FunctionComponent<{
	link: NonNullable<HeaderProps['links']>[number]
	setHoveredLinkPosition: (position: { offsetLeft: number; width: number } | null) => void
	setActiveLinkPosition: (position: { offsetLeft: number; width: number } | null) => void
}> = ({ link, setHoveredLinkPosition, setActiveLinkPosition }) => {
	const linkRef = useRef<HTMLDivElement | null>(null)

	useEffect(() => {
		if (!link?.isActive) {
			return
		}
		if (!linkRef.current) {
			return
		}
		setActiveLinkPosition({
			offsetLeft: linkRef.current.offsetLeft + linkRef.current.offsetWidth / 2,
			width: linkRef.current.offsetWidth,
		})
		const handleResize = () => {
			if (!linkRef.current) {
				return
			}
			setActiveLinkPosition({
				offsetLeft: linkRef.current.offsetLeft + linkRef.current.offsetWidth / 2,
				width: linkRef.current.offsetWidth,
			})
		}
		window.addEventListener('resize', handleResize)
		return () => window.removeEventListener('resize', handleResize)
	}, [link?.isActive, setActiveLinkPosition])

	if (!link) {
		return null
	}

	// Cast to any to access optional fields coming from website schema
	// biome-ignore lint/suspicious/noExplicitAny: cross-schema object, dynamic keys needed
	const extendedLink: any = link
	const isLinkSubmenu = Boolean(extendedLink.nestedLinks) || extendedLink.linkListItemType === 'products'

	return (
		<li className={styles.item}>
			{isLinkSubmenu ? (
				<button
					type="button"
					className={clsx(styles.link, styles.linkNoPadding)}
					onMouseEnter={() => {
						if (!linkRef.current) {
							return
						}
						setHoveredLinkPosition({
							offsetLeft: linkRef.current.offsetLeft + linkRef.current.offsetWidth / 2,
							width: linkRef.current.offsetWidth,
						})
					}}
				>
					<Link
						className={styles.link}
						onMouseEnter={() => {
							if (!linkRef.current) {
								return
							}
							setHoveredLinkPosition({
								offsetLeft: linkRef.current.offsetLeft + linkRef.current.offsetWidth / 2,
								width: linkRef.current.offsetWidth,
							})
						}}
						{...headerLinkToHrefTargetRel(link)}
					>
						<div ref={linkRef} className={styles.linkIn}>
							{link.title ?? contemberLinkToHref(link)}
							<div className={styles.chevron}>
								<Icon name="chevron" />
							</div>
						</div>
					</Link>
				</button>
			) : (
				<Link
					className={styles.link}
					onMouseEnter={() => {
						if (!linkRef.current) {
							return
						}
						setHoveredLinkPosition({
							offsetLeft: linkRef.current.offsetLeft + linkRef.current.offsetWidth / 2,
							width: linkRef.current.offsetWidth,
						})
					}}
					{...headerLinkToHrefTargetRel(link)}
				>
					<div className={styles.linkIn} ref={linkRef}>
						{link.title ?? contemberLinkToHref(link)}
					</div>
				</Link>
			)}

			{/* Render nested submenu */}
			{extendedLink.linkListItemType === 'default' && extendedLink.nestedLinks ? (
				<HeaderNestedLinks items={extendedLink.nestedLinks.items} />
			) : extendedLink.linkListItemType === 'products' && extendedLink.listProduct?.length > 0 ? (
				<HeaderSubmenuProducts
					items={extendedLink.listProduct}
					isFullProductsButton={extendedLink.isFullProductsButton}
					productsPageLink={{
						id: extendedLink.id,
						isTargetBlank: extendedLink.isTargetBlank,
						externalLink: extendedLink.externalLink,
						internalLink: extendedLink.internalLink,
						title: extendedLink.title,
						type: extendedLink.type,
					}}
				/>
			) : null}
		</li>
	)
}
